import {useCallback, useEffect, useRef, useState} from 'react'
import {evaluateAchievements, markAchievementsSeen} from '../util/achievements'

/*
 * Detects newly earned achievement badges and holds the announce queue.
 *
 * Shared by the dashboard (evaluates on mount) and the question screen (evaluates after each
 * answer) so there is one implementation of "did they just earn something, and have we told
 * them yet".
 *
 * The server is authoritative: evaluation is idempotent, and a badge stays in
 * `unannounced` until the client confirms it was shown. That means a badge earned while the
 * tab was closed is still announced on the next visit instead of being lost.
 *
 * Usage:
 *   const {current, dismiss, evaluate, earnedCount} = useAchievementAlerts(prepKitId, {evaluateOnMount: true})
 */
const useAchievementAlerts = (prepKitId, {evaluateOnMount = false} = {}) => {
	const [queue, setQueue] = useState([])
	const [earnedCount, setEarnedCount] = useState(null)

	// tokens already queued this session, so a repeat evaluate cannot double-announce
	const queuedTokens = useRef(new Set())
	// guards against React 18 StrictMode double-invoking the mount effect
	const evaluatedOnMount = useRef(false)

	const evaluate = useCallback(async () => {
		if (!prepKitId) return

		const result = await evaluateAchievements(prepKitId)
		if (!result) return

		setEarnedCount(result.earnedCount)

		const pending = (result.unannounced || []).filter(a => !queuedTokens.current.has(a.token))
		if (!pending.length) return

		pending.forEach(a => queuedTokens.current.add(a.token))
		setQueue(prev => [...prev, ...pending])
	}, [prepKitId])

	useEffect(() => {
		if (!evaluateOnMount || evaluatedOnMount.current) return
		evaluatedOnMount.current = true
		evaluate()
	}, [evaluateOnMount, evaluate])

	// reset when switching prepkits: badges are prepkit-scoped
	useEffect(() => {
		queuedTokens.current = new Set()
		evaluatedOnMount.current = false
		setQueue([])
		setEarnedCount(null)
	}, [prepKitId])

	/*
	 * Announced -> tell the server, then move to the next badge. Marking seen is
	 * deliberately not awaited before advancing: the queue is local, and a failed mark just
	 * means the badge is announced again next visit, which is better than blocking the UI.
	 */
	const dismiss = useCallback(() => {
		setQueue(prev => {
			const [shown, ...rest] = prev
			if (shown) markAchievementsSeen(prepKitId, [shown.token])
			return rest
		})
	}, [prepKitId])

	return {
		current: queue[0] || null,
		remaining: Math.max(0, queue.length - 1),
		earnedCount,
		evaluate,
		dismiss,
	}
}

export default useAchievementAlerts
